import { authenticatedWorkspaceGuard } from "app/core/services/workspace/workspace.guard";
import { initialDataResolver } from "app/app.resolvers";
import { AuthGuard } from "shared-service/services/auth/guards/auth.guard";
import { NoAuthGuard } from "shared-service/services/auth/guards/noAuth.guard";
import { EmptyLayoutComponent } from "app/layout/empty-layout.component";
import { VlpLayoutComponent } from "app/layout/vlp-layout/vlp-layout.component";
import { eSystemFunction } from "app/core/enums/shipment-management/enum-shipment-report.enum";
import { functionGuard } from "app/core/services/auth/function.guard";

import { Route } from "@angular/router";

// @formatter:off


export const appRoutes: Route[] = [
    // The overview is the single canonical landing dashboard.
    { path: "", pathMatch: "full", redirectTo: "dashboards/overview" },

    // Redirect signed-in user to the canonical overview.
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: "signed-in-redirect", pathMatch: "full", redirectTo: "dashboards/overview" },

    // Auth routes for guests
    {
        path: "",
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: EmptyLayoutComponent,
        children: [
            { path: "sign-in", loadChildren: () => import("app/modules/auth/sign-in/sign-in.routes") },
            { path: "password-reset", loadChildren: () => import("app/modules/auth/password-reset/password-reset.routes") },
        ],
    },

    // SL-5: public activation page (no guard — invitee is not signed in, but a signed-in admin may open it too)
    {
        path: "",
        component: EmptyLayoutComponent,
        children: [
            { path: "tenant-activate", loadChildren: () => import("app/modules/auth/tenant-activate/tenant-activate.routes") },
        ],
    },

    // Auth routes for authenticated users
    {
        path: "",
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: EmptyLayoutComponent,
        children: [{ path: "sign-out", loadChildren: () => import("app/modules/auth/sign-out/sign-out.routes") }],
    },

    // Admin routes
    {
        path: "",
        canActivate: [authenticatedWorkspaceGuard],
        canActivateChild: [authenticatedWorkspaceGuard],
        runGuardsAndResolvers: "paramsOrQueryParamsChange",
        component: VlpLayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: "access-denied",
                loadComponent: () => import("app/modules/auth/access-denied/access-denied.component")
                    .then((module) => module.AccessDeniedComponent),
            },
            { path: "work", data: { breadcrumb: "work.title" }, loadComponent: () => import("app/modules/work-management/work-page.component").then(m => m.WorkPageComponent) },
            // Profile
            {
                path: "profile",
                children: [
                    {
                        path: "sign-out",
                        pathMatch: "full",
                        redirectTo: "/sign-out",
                    },
                ],
            },

            //Dashboards
            { path: "dashboards", children: [{ path: "home", pathMatch: "full", redirectTo: "overview" }] },
            { path: "dashboards", children: [{ path: "overview", loadChildren: () => import("app/modules/dashboards/home/home.routes") }] },
            { path: "dashboards", children: [{ path: "multi_level", loadChildren: () => import("app/modules/dashboards/multi-level-dashboard/multi-level-dashboard.routes") }] },

            //Systems
            { path: "systems", children: [{ path: "country", loadChildren: () => import("app/modules/systems/country-info-system/country-info-system.routes") }] },
            { path: "systems", children: [{ path: "goods_type", loadChildren: () => import("app/modules/systems/goods-type-system/goods-type-system.routes") }] },
            { path: "systems", children: [{ path: "hs_code", loadChildren: () => import("app/modules/systems/hs-code-system/hs-code-system.routes") }] },
            { path: "systems", children: [{ path: "trade_compliance", loadChildren: () => import("app/modules/systems/trade-compliance-system/trade-compliance-system.routes") }] },
            { path: "systems", children: [{ path: "system_setting", loadChildren: () => import("app/modules/systems/system-setting-system/system-setting-system.routes") }] },
            { path: "systems", children: [{ path: "measurement_conversion", loadChildren: () => import("app/modules/systems/measurement-conversion-system/measurement-conversion-system.routes") }] },
            { path: "systems", children: [{ path: "wallet", loadChildren: () => import("app/modules/systems/wallet-system/wallet-system.routes") }] },
            { path: "systems", children: [{ path: "financial", loadChildren: () => import("app/modules/systems/financial-system/financial-system.routes") }] },
            { path: "systems", children: [{ path: "payment_method", loadChildren: () => import("app/modules/systems/payment-configuration-system/payment-configuration-system.routes") }] },
            { path: "systems", children: [{ path: "voucher", loadChildren: () => import("app/modules/systems/voucher-system/voucher-system.routes") }] },

            // Services — keep one canonical route tree so route discovery and lazy navigation agree.
            { path: "services/vendor_management", redirectTo: "services/vendors/management", pathMatch: "full" },
            {
                path: "services",
                children: [
                    // SL-359 — Vendor redesign và các cấu hình vận hành Vendor tại cùng một route tree.
                    { path: "vendors", loadChildren: () => import("app/modules/services/vendors/vendors.routes") },
                    { path: "core_logistics_service", loadChildren: () => import("app/modules/services/core-logistics-service-management/core-logistics-service-management.routes") },
                    { path: "selling_prices", pathMatch: "full", redirectTo: "pricing" },
                    // SL-361 — Pricing Workspace (Trung tâm bảng giá): server-paged customer price book grid + detail.
                    { path: "pricing", loadChildren: () => import("app/modules/services/pricing-workspace/pricing-workspace.routes") },
                    { path: "routing_admin", loadChildren: () => import("app/modules/services/routing-admin/routing-admin.routes") },
                    // Fulfillment Plan is edited exclusively inside the Core Logistics Service wizard.
                    // Keep redirects for old bookmarks, but do not register a standalone endpoint: the
                    // navigation service consequently hides the former duplicate menu item.
                    { path: "fulfillment_plan", pathMatch: "full", redirectTo: "core_logistics_service" },
                    { path: "fulfillment_plan/designer", pathMatch: "full", redirectTo: "core_logistics_service" },
                    { path: "fulfillment_plan/simulator", pathMatch: "full", redirectTo: "core_logistics_service" },
                    { path: "vendor_integration", loadChildren: () => import("app/modules/services/vendor-integration/vendor-integration.routes") },
                    { path: "receivable", loadChildren: () => import("app/modules/shipment/receivable-management/receivable-management.routes") },
                ],
            },

            //Systems
            {
                path: "systems",
                children: [{
                    path: "account_group",
                    data: { accountGroupKindId: 2, requiredFunctionId: eSystemFunction.SYSTEM_FUNCTION_CUSTOMER_ACCOUNT_GROUP },
                    loadChildren: () => import("app/modules/systems/account-group-system/account-group-system.routes"),
                }],
            },
            {
                path: "customer",
                children: [
                    { path: "customer_management", loadChildren: () => import("app/modules/customer/customer-management/customer-management.routes") },
                    {
                        path: "shipment_account_groups",
                        pathMatch: "full",
                        redirectTo: "/systems/account_group",
                    },
                    {
                        path: "shipment_accounts",
                        pathMatch: "full",
                        redirectTo: "/systems/account",
                    },
                ],
            },
            // Canonical SaaS workspaces. Legacy routes remain as redirects so existing bookmarks keep working.
            { path: "platform_customer/ops/:id", pathMatch: "full", redirectTo: "platform/customers/:id" },
            { path: "platform_customer/detail/:id", pathMatch: "full", redirectTo: "platform/customers/:id" },
            { path: "platform_customer/ops", pathMatch: "full", redirectTo: "platform/customers" },
            { path: "platform_customer", pathMatch: "full", redirectTo: "platform/customers" },
            { path: "platform", children: [{ path: "customers", loadChildren: () => import("app/modules/platform-customer/platform-customer.routes") }] },
            // Cargo admin pages (rule sets, carrier remote-area lists) are hidden for now. Leaving the route unregistered
            // is what hides them: NavigationService drops a menu entry whose route is not registered, so the Cargo menu
            // item (function 228) disappears without a data change, and the module stays in the codebase. The public
            // Swagger URL they no longer manage lives in System settings (Cargo / CargoPublicApiBaseUrl). To bring the
            // pages back, register again:
            //   { path: "platform", children: [{ path: "carrier-intelligence", loadChildren: () => import("app/modules/carrier-intelligence/carrier-intelligence.routes") }] },
            { path: "platform/customer-integrations", loadChildren: () => import("app/modules/customer-integration/customer-integration.routes") },
            // Reporting upgrade Phase 3-5 — analytics & reporting module (functionGuard 900-910 per route).
            { path: "report/overview", pathMatch: "full", redirectTo: "reports/overview" },
            // SL-1545 — the reporting menu group's own title, so a report page opened from the overview reads
            // "Báo cáo & Phân tích > <page>" like the ones reached from the menu, not "reports > <url segment>".
            { path: "reports", data: { breadcrumb: "Reporting" }, loadChildren: () => import("app/modules/reporting/reporting.routes") },
            { path: "systems/tenant_admin", pathMatch: "full", redirectTo: "workspace/overview" },
            { path: "workspace", children: [{ path: "usage", data: { breadcrumb: "usagePage.title" }, loadChildren: () => import("app/modules/systems/workspace-usage/workspace-usage.routes") }, { path: "overview", loadChildren: () => import("app/modules/systems/tenant-admin/tenant-admin.routes") }] },
            { path: "content/tenant_site/editor/:id", pathMatch: "full", redirectTo: "content/sites/editor/:id" },
            { path: "content/tenant_site", pathMatch: "full", redirectTo: "content/sites" },
            { path: "content", children: [{ path: "sites", loadChildren: () => import("app/modules/content/tenant-site/tenant-site.routes") }] },
            { path: "network", children: [{ path: "network_workspace", loadChildren: () => import("app/modules/facility/network-workspace/network-workspace.routes") }] },
            {
                path: "systems",
                children: [{
                    path: "account",
                    data: { accountUsageTypeId: 2, requiredFunctionId: eSystemFunction.SYSTEM_FUNCTION_CUSTOMER_ACCOUNT },
                    loadChildren: () => import("app/modules/systems/account-system/account-system.routes"),
                }],
            },
            { path: "systems", children: [{ path: "currency", loadChildren: () => import("app/modules/systems/currency-system/currency-system.routes") }] },
            { path: "systems", children: [{ path: "exchange_rate", loadChildren: () => import("app/modules/systems/exchange-rate-system/exchange-rate-system.routes") }] },
            { path: "systems", children: [{ path: "tenant_reference_policy", canActivate: [functionGuard(eSystemFunction.SYSTEM_FUNCTION_TENANT_ADMIN)], loadChildren: () => import("app/modules/systems/tenant-reference-data-policy-system/tenant-reference-data-policy.routes") }] },
            // B8: quản trị mẫu DOCX của chứng từ thương mại. Gác theo quyền Lô hàng vì backend
            // (ExternalShipmentTradeDocumentController) cũng gác bằng eSystemFunction.SYSTEM_FUNCTION_SHIPMENT_MANAGEMENT.
            {
                path: "systems",
                children: [{
                    path: "trade_document_template",
                    canActivate: [functionGuard(eSystemFunction.SYSTEM_FUNCTION_SHIPMENT_MANAGEMENT)],
                    data: { requiredFunctionId: eSystemFunction.SYSTEM_FUNCTION_SHIPMENT_MANAGEMENT },
                    loadChildren: () => import("app/modules/systems/trade-document-template-system/trade-document-template-system.routes"),
                }],
            },
            { path: "systems", children: [{ path: "notification", loadChildren: () => import("app/modules/systems/notification/notification-admin.routes") }] }, // LUL-653
            { path: "systems", children: [{ path: "role_permission", loadChildren: () => import("app/modules/systems/role-permission-system/role-permission-system.routes") }] },
            { path: "notifications", loadChildren: () => import("app/modules/notification-inbox/notification-list.routes") },
            { path: "systems", children: [{ path: "warehouse_address", loadChildren: () => import("app/modules/systems/core-logistics-warehouse-address-system/core-logistics-warehouse-address-system.routes") }] },
            { path: "systems", children: [{ path: "balances", loadChildren: () => import("app/modules/systems/balances-system/balances-system.routes") }] },

            //Shipment
            {
                path: "shipment",
                children: [
                    // Consolidated shipment report routes - uses eReportShipmentType for type parameter
                    { path: "shipment_report", loadChildren: () => import("app/modules/shipment/shipment-report/shipment-report.routes") },
                    // VLP fulfillment result pipeline — Shipment result detail (2 tab: vận chuyển + tài chính).
                    {
                        path: "shipment_result/:shipmentInfoId",
                        canActivate: [functionGuard(eSystemFunction.SYSTEM_FUNCTION_SHIPMENT_MANAGEMENT)],
                        loadComponent: () =>
                            import("app/modules/shipment/shipment-result-detail/shipment-result-detail.component").then(
                                (module) => module.ShipmentResultDetailComponent,
                            ),
                    },
                    // Canonical redirects for legacy SystemFunction routes still present in older tenant data.
                    { path: "shipment_report_by_status", pathMatch: "full", redirectTo: "shipment_report/report/0" },
                    { path: "shipment_report_core_logistics_service", pathMatch: "full", redirectTo: "shipment_report/report/1" },
                    { path: "shipment_report_vendor_service", pathMatch: "full", redirectTo: "shipment_report/report/2" },
                    { path: "shipment_report_by_weight", pathMatch: "full", redirectTo: "shipment_report/report/3" },
                    { path: "shipment_report_by_zone", pathMatch: "full", redirectTo: "shipment_report/report/4" },
                    { path: "shipment_report_by_location", pathMatch: "full", redirectTo: "shipment_report/report/5" },
                    { path: "shipment_report_by_entry_point", pathMatch: "full", redirectTo: "shipment_report/report/6" },
                    { path: "shipment_report_by_vendor", pathMatch: "full", redirectTo: "shipment_report/report/7" },
                    { path: "waybill_report", pathMatch: "full", redirectTo: "shipment_report/report/0" },
                    { path: "shipment_management", loadChildren: () => import("app/modules/shipment/shipment-management/shipment-management.routes") },
                    {
                        path: "cancellation_refunds",
                        canActivate: [functionGuard(eSystemFunction.SYSTEM_FUNCTION_CANCELLATION_REFUND)],
                        loadComponent: () =>
                            import("app/modules/shipment/cancellation-refund/cancellation-refund.component").then(
                                (module) => module.CancellationRefundComponent,
                            ),
                    },
                    { path: "shipment_create", loadChildren: () => import("app/modules/shipment/shipment-create/shipment-create.routes") },
                    { path: "shipment_import", loadChildren: () => import("app/modules/shipment/shipment-import/shipment-import.routes") },
                    { path: "shipment_reconciliation", loadChildren: () => import("app/modules/shipment/shipment-reconciliation/shipment-reconciliation.routes") },
                    { path: "shipment_adjustment", loadChildren: () => import("app/modules/shipment/shipment-adjustment/shipment-adjustment.routes") },
                    { path: "receivable_management/statements", pathMatch: "full", redirectTo: "/services/receivable/statements" },
                    { path: "receivable_management", pathMatch: "full", redirectTo: "/services/receivable" },
                    { path: "tracking_label_management", canActivate: [functionGuard(eSystemFunction.SYSTEM_FUNCTION_LABEL_TEMPLATE_MANAGEMENT)], loadChildren: () => import("app/modules/shipment/tracking-label-management/tracking-label-management.routes") },
                    { path: "shipment_quotation", loadChildren: () => import("app/modules/shipment/shipment-quotation/shipment-quotation.routes") },
                    { path: "tracking_event", data: { workspaceTenantAlias: "tenantId" }, loadChildren: () => import("app/modules/shipment/tracking-event/tracking-event.routes") }, // LUL-556
                    { path: "tracking_automation", loadChildren: () => import("app/modules/shipment/tracking-automation/tracking-automation.routes") },
                ],
            },
            {
                path: "billing",
                children: [
                    { path: "invoices", loadChildren: () => import("app/modules/billing/invoice/invoice.routes") },
                    // Đối soát tài chính theo shipment (chứng từ do BillingManagement sở hữu).
                    {
                        path: "financial_reconciliation",
                        loadChildren: () => import("app/modules/billing/financial-reconciliation/financial-reconciliation.routes"),
                    },
                ],
            },

            //Dispatch / Manifest (LUL-502)
            { path: "dispatch", loadChildren: () => import("app/modules/dispatch/dispatch.routes") },
            {
                path: "profile",
                children: [{ path: "update-profile", loadChildren: () => import("app/modules/systems/update-profile/update-profile.routes") }],
            },

            //Document
            { path: "document", children: [{ path: "manual-document", loadChildren: () => import("app/modules/document/instruction-manual/instruction-manual.routes") }] },
            { path: "document", children: [{ path: "about", loadChildren: () => import("app/modules/document/about/about.routes") }] },


            //Commission
            { path: "commission", children: [{ path: "commission_ledger", canActivate: [functionGuard(eSystemFunction.SYSTEM_FUNCTION_COMMISION_LEDGER)], loadChildren: () => import("app/modules/commission/commission-ledger/commission-ledger.routes") }] },
            { path: "commission", children: [{ path: "my_commission", canActivate: [functionGuard(eSystemFunction.SYSTEM_FUNCTION_MY_COMMISSION)], loadChildren: () => import("app/modules/commission/my-commission/my-commission.routes") }] },
            { path: "commission", children: [{ path: "commission_payout", canActivate: [functionGuard(eSystemFunction.SYSTEM_FUNCTION_COMMISSION_PAYOUT_BATCH)], loadChildren: () => import("app/modules/commission/commission-payout/commission-payout.routes") }] },
            { path: "commission", children: [{ path: "commission_payout_batch", canActivate: [functionGuard(eSystemFunction.SYSTEM_FUNCTION_COMMISSION_PAYOUT_BATCH)], loadChildren: () => import("app/modules/commission/commission-payout-batch/commission-payout-batch.routes") }] },
            { path: "commission", children: [{ path: "commission_policy", canActivate: [functionGuard(eSystemFunction.SYSTEM_FUNCTION_COMMISSION_POLICY)], loadChildren: () => import("app/modules/commission/commission-policy/commission-policy.routes") }] },
            { path: "commission", children: [{ path: "commission_simulator", canActivate: [functionGuard(eSystemFunction.SYSTEM_FUNCTION_COMMISSION_SIMULATOR)], loadChildren: () => import("app/modules/commission/commission-simulator/commission-simulator.routes") }] },
            { path: "commission", children: [{ path: "commission_beneficiary_assignment", canActivate: [functionGuard(eSystemFunction.SYSTEM_FUNCTION_COMMISSION_BENEFICIARY_ASSIGNMENT)], loadChildren: () => import("app/modules/commission/commission-beneficiary-assignment/commission-beneficiary-assignment.routes") }] },
            { path: "commission", children: [{ path: "commission_report", canActivate: [functionGuard(eSystemFunction.SYSTEM_FUNCTION_COMMISSION_REPORT)], loadChildren: () => import("app/modules/commission/commission-report/commission-report.routes") }] },
            { path: "commission", children: [{ path: "commission_reconciliation", canActivate: [functionGuard(eSystemFunction.SYSTEM_FUNCTION_COMMISSION_RECONCILIATION_VIEW)], loadChildren: () => import("app/modules/commission/commission-reconciliation/commission-reconciliation.routes") }] },
            { path: "commission", children: [{ path: "commission_event_operations", canActivate: [functionGuard(eSystemFunction.SYSTEM_FUNCTION_COMMISSION_EVENT_REPLAY)], loadChildren: () => import("app/modules/commission/commission-event-operations/commission-event-operations.routes") }] },
        ],
    },
];
